import {
  createSearchParams,
  generatePath,
  Link,
  useNavigate,
} from "react-router-dom"
import { computed, observable } from "mobx"
import { observer } from "mobx-react"
import { Button, Col, Flex, Row, Space, Tag, Typography } from "antd"

import { WalletItem } from "@src/app/balanceStore"
import { CurrencyIcon } from "@src/app/components/CurrencyIcon"
// import { SandClockIcon } from "@src/app/components/icons/SandClockIcon"
import { SwapIcon } from "@src/app/components/icons/SwapIcon"
import { TopUpIcon } from "@src/app/components/icons/TopUpIcon"
import { WithdrawIcon } from "@src/app/components/icons/WithdrawIcon"
import {
  Currency,
  GlobalCurrenciesCollection,
} from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import uiStore from "@src/app/uiStore"
import css from "@src/pages/balances/index.module.css"
import { GlobalConvertPairsCollection } from "@src/pages/convert/classes/ConvertPairsCollection"
import { capitalizeFirstLetter } from "@src/pages/settingsWhitelist/capitalizeFirstLetter"

type Props = {
  wallet: WalletItem
}

export const BalancesCard = observer(({ wallet }: Props) => {
  const nav = useNavigate()
  const { balances, network, symbol } = wallet
  const currency = GlobalCurrenciesCollection()
  const convertPairs = GlobalConvertPairsCollection()
  const { display_name, id, symbol_alias, token_kind } = computed(
    () =>
      currency.getBySymbolNetwork({ network, symbol }) ?? {
        decimals: 0,
        display_name: "",
        id: "",
        network,
        symbol,
        symbol_alias: "",
        token_kind: "",
        tx_explorer_url: "",
      },
  ).get()

  const { isTransferioTheme } = uiStore()

  // Pending balances are temporarily hidden.
  // const isPendingBalance = computed(() => Number(balances.diff) > 0).get()

  const generateDepositPath = (network: string, symbol: string) => {
    return generatePath(
      ["fiat"].includes(network) ? paths.depositFiat : paths.deposit,
      { network, symbol },
    )
  }

  const generateWithdrawPath = (network: string, symbol: string) => {
    return generatePath(
      ["bank", "card", "fiat"].includes(network)
        ? paths.withdrawFiat
        : paths.withdraw,
      { network, symbol },
    )
  }

  const generateConvertPath = (
    currency: string,
    mode: "from" | "to" = "from",
  ) => {
    return `${paths.swap}?${createSearchParams({ currency, mode })}`
  }

  const getNetwork = (network: string) => {
    if (network == "bank") {
      return "bank transfer"
    }

    return network
  }

  const isCardOrBank = computed(
    () => ["bank", "card"].indexOf(network) !== -1,
  ).get()

  const canSwap = computed(() =>
    convertPairs.hasFromPair({ network, symbol }),
  ).get()

  const onTopup = () => {
    if (isCardOrBank) {
      return nav(generateConvertPath(id, "to"))
    }

    nav(generateDepositPath(network, symbol))
  }

  return (
    <Row
      align="middle"
      className={css.card}
      gutter={16}
      justify="space-between"
    >
      <Col span={8}>
        <Flex align="center" gap={16}>
          <CurrencyIcon network={network} symbol={symbol} />
          <Flex vertical>
            <Typography.Text className={css.balance}>
              {balances.available}{" "}
              {["card", "fiat"].includes(network) ? symbol : symbol_alias}
            </Typography.Text>
            <Flex gap={12}>
              <Typography.Text className={css.availableSymbol}>
                {["bank", "card"].includes(network)
                  ? capitalizeFirstLetter(getNetwork(network))
                  : network !== "fiat" && display_name}
              </Typography.Text>
              {token_kind && (
                <Tag color={"blue"} style={{ paddingLeft: 6 }}>
                  {token_kind}
                </Tag>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Col>

      {/* Pending balances are temporarily hidden.
      {isPendingBalance && (
        <Col span={6}>
          <Flex vertical>
            <Flex align="center" gap={4}>
              <SandClockIcon />
              <Typography.Text className={css.pendingText}>
                Pending balance
              </Typography.Text>
            </Flex>
            <Typography.Text className={css.pending}>
              {balances.diff} {symbol_alias}
            </Typography.Text>
          </Flex>
        </Col>
      )}
      */}

      {/* Use span={isPendingBalance ? 10 : 16} when pending balances are enabled. */}
      <Col span={16}>
        {display_name ? (
          <Space className={css.buttonWrapper} size="small">
            <Button
              className={css.link}
              id="GENERATED_ID_BALANCEPAGE_LINK_VOID_1"
              onClick={() => onTopup()}
              type={"text"}
            >
              <Flex align="center" gap={4} justify="center" vertical>
                <TopUpIcon />
                <Typography.Text className={css.buttonText}>
                  Top Up
                </Typography.Text>
              </Flex>
            </Button>
            {!isTransferioTheme && canSwap && (
              <Link
                className={css.link}
                id="GENERATED_ID_BALANCEPAGE_LINK_VOID_2"
                to={generateConvertPath(id)}
              >
                <Flex align="center" gap={4} justify="center" vertical>
                  <SwapIcon />
                  <Typography.Text className={css.buttonText}>
                    Swap
                  </Typography.Text>
                </Flex>
              </Link>
            )}
            {!isCardOrBank && (
              <Link
                className={css.link}
                id="GENERATED_ID_BALANCEPAGE_LINK_VOID_3"
                to={generateWithdrawPath(network, symbol)}
              >
                <Flex align="center" gap={4} justify="center" vertical>
                  <WithdrawIcon />
                  <Typography.Text className={css.buttonText}>
                    Withdraw
                  </Typography.Text>
                </Flex>
              </Link>
            )}
          </Space>
        ) : null}
      </Col>
    </Row>
  )
})
